import { Restaurant } from "api-types";

import React, { useCallback, useMemo } from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { AppBar, Avatar, Box, IconButton, Tab, Tabs } from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  ArrowBackRounded,
  CampaignRounded,
  ColorLensRounded,
  IntegrationInstructionsRounded,
  LiquorRounded,
  MenuBookRounded,
  RoomServiceRounded,
  SettingsOutlined,
} from "@mui/icons-material";

import {
  useDashboardAppFeatureFlag,
  useFeatures,
} from "@dashboard/hooks/useFeature";
import type { ReduxState } from "@dashboard/types";

type TabConfig = {
  label: string;
  icon: React.ReactElement;
  path: string;
  dataDdActionName: string;
};

// Return the ordered tabs for the header sidebar
const getTabConfig = (
  hasProducts: boolean,
  showIntegrationsPage: boolean
): TabConfig[] => {
  let tabs = [
    {
      label: "Menu",
      icon: <MenuBookRounded fontSize={"small"} />,
      path: "/menus",
      dataDdActionName: "menu-page",
    },
  ];

  if (hasProducts) {
    tabs.push({
      label: "Products",
      icon: <LiquorRounded fontSize={"small"} />,
      path: "/products",
      dataDdActionName: "products-page",
    });
  }

  tabs.push({
    label: "Dishes",
    icon: <RoomServiceRounded fontSize={"small"} />,
    path: "/dishes",
    dataDdActionName: "dishes-page",
  });

  tabs.push({
    label: "Branding",
    icon: <ColorLensRounded fontSize={"small"} />,
    path: "/branding",
    dataDdActionName: "branding-page",
  });

  tabs.push({
    label: "Marketing",
    icon: <CampaignRounded fontSize={"small"} />,
    path: "/marketing",
    dataDdActionName: "marketing-page",
  });

  if (showIntegrationsPage) {
    tabs.push({
      label: "Integrations",
      icon: <IntegrationInstructionsRounded fontSize={"small"} />,
      path: "/integrations",
      dataDdActionName: "integrations-page",
    });
  }

  tabs.push({
    label: "Settings",
    icon: <SettingsOutlined fontSize={"small"} />,
    path: "/settings",
    dataDdActionName: "settings-page",
  });

  return tabs;
};

export const HeaderSidebarComponent = ({
  isLargeScreen,
  isLeftDrawerOpen,
  toggleLeftDrawer,
  pathname,
  navigate,
  selectedRestaurant,
}: {
  isLargeScreen: boolean;
  isLeftDrawerOpen: boolean;
  toggleLeftDrawer: () => void;
  pathname: string;
  navigate: (to: string) => void;
  selectedRestaurant: Restaurant;
}) => {
  const { products } = useFeatures();
  const showIntegrationsPage = useDashboardAppFeatureFlag(
    "showIntegrationsPage"
  );
  const tabConfig = useMemo(
    () => getTabConfig(products, showIntegrationsPage),
    [products, showIntegrationsPage]
  );

  const [tab, setTab] = React.useState<false | number>(false);

  useEffect(() => {
    const tabIdx = tabConfig.findIndex((tab) => pathname.startsWith(tab.path));
    setTab(tabIdx === -1 ? false : tabIdx);
  }, [pathname, tabConfig]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const navCallback = useCallback(
    (to: string) => {
      isLeftDrawerOpen && toggleLeftDrawer();
      navigate(to);
    },
    [isLargeScreen, isLeftDrawerOpen, toggleLeftDrawer, navigate]
  );

  return (
    <AppBar
      position="sticky"
      sx={({ palette }) => ({
        backgroundColor: palette.grey[50],
        boxShadow: "none",
        pt: isLargeScreen ? 0 : 2.5,
      })}
    >
      {!isLargeScreen && (
        <Box sx={{ display: "flex", alignSelf: "center", p: 1 }}>
          <IconButton onClick={toggleLeftDrawer}>
            <ArrowBackRounded fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ pt: isLargeScreen ? 8 : 4 }}
      >
        <RestaurantAvatar name={selectedRestaurant.name} />
      </Box>
      <Tabs
        TabIndicatorProps={{
          sx: ({ palette }) => ({
            backgroundColor: palette.secondary.main,
            color: palette.grey[400],
          }),
        }}
        sx={{ pt: 4 }}
        orientation="vertical"
        textColor={"secondary"}
        value={tab}
        onChange={handleTabChange}
        centered
      >
        {tabConfig.map((tab) => (
          <StyledTabs
            key={tab.path}
            label={tab.label}
            icon={tab.icon}
            onClick={() => navCallback(tab.path)}
            data-dd-action-name={tab.dataDdActionName}
          />
        ))}
      </Tabs>
    </AppBar>
  );
};

const StyledTabs = styled(Tab)(({ theme }) => ({
  color: theme.palette.grey.A700,
  textAlign: "center",
  fontSize: "12px",
  lineHeight: 1.5,
}));

const RestaurantAvatar = ({ name }: { name: string }) => {
  //only taking initial on first word. e.g. "McDonalds" -> "M"
  const restaurantInitials = name.charAt(0).toUpperCase();

  // check if the logo image exists in branding, if so, use that
  const logoImageUrl = useSelector(
    (state: ReduxState) => state.branding.branding?.logoImageUrl || undefined
  );

  return (
    <Avatar
      sx={({ palette }) => ({
        bgcolor: logoImageUrl ? "transparent" : palette.primary.main,
      })}
    >
      {logoImageUrl ? (
        <img
          src={logoImageUrl}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        restaurantInitials
      )}
    </Avatar>
  );
};
